// ────────────────────────────────────────────────────────────────────────────
// E49 — on-device scan-pipeline self-report telemetry.
//
// The iPhone 13 stall (TestFlight) presented as: overlay launches, shows
// "Scanning..." with a green dot, but the body stays EMPTY for minutes and
// the count never moves. With no device telemetry there was no way to tell
// WHICH stage produced zero detections: were frames even arriving? were they
// reaching OCR or being dropped by the pre-OCR gate? was OCR running and
// finding nothing?
//
// This is a tiny module-level counter store the live-scan loop updates on
// EVERY frame (not only frames that pass the gate, which is the whole point:
// when the gate stalls, the per-frame diagnostics snapshot is never published,
// so the gate's own activity has to be recorded here independently). The
// Settings -> Live Scan diagnostics panel reads it so a tester can self-report
// the counters from the device without a dev rebuild.
//
// Pure module-level state: no React, no persistence, no I/O on the hot path.
// Reset at session start; read on demand. Counter bumps are O(1) integer adds
// so there is no scan-perf regression.
// ────────────────────────────────────────────────────────────────────────────

// Vision-only trial: the pre-OCR palette gate (dexPreOcrGate) was removed, so
// the gate-class/action union it exported is gone. The gate-verdict telemetry
// fields are retyped to a self-contained string form and a `pillOcrSource`
// field records where the pill/chip variant came from this frame (fresh Vision
// read, sticky cache, or none).
export type PillOcrSource = "fresh" | "sticky" | "none";

export interface ScanPipelineTelemetry {
  /**
   * Frames suppressed as byte-identical duplicates BEFORE any OCR (a held-steady
   * screen the 1 Hz extension re-emitted, or a copy of a frame already in
   * flight). Pure wasted-work avoided; never a dropped read — see dexFrameDedup.
   */
  framesSkippedDuplicate: number;
  /**
   * Frames whose pre-OCR page-identity (dex colour family / process-vs-drop
   * verdict) was served from the page-identity cache instead of re-running the
   * decode + canvas readback + palette classification. A cache hit means the
   * frame's pixels were byte-identical to a recently-classified frame this
   * session (e.g. a back-scroll A→B→A), so the verdict is provably the same and
   * the classify CPU was saved. Never a changed read — see dexPageIdentityCache.
   */
  framesPageDetectCached: number;
  /** Frames the loop received and attempted to classify (post blur/scroll gate). */
  framesReceived: number;
  /** Frames the pre-OCR gate allowed through to the OCR fanout. */
  framesPassedGate: number;
  /** Frames the pre-OCR gate dropped (did not reach OCR). */
  framesDroppedGate: number;
  /** OCR fanout invocations (one per gate-passed frame that reached extract). */
  ocrAttempts: number;
  /** OCR invocations that yielded at least one Pokedex number. */
  ocrHits: number;
  /**
   * Vision-only trial: replaces the deleted pre-OCR gate class/action verdict.
   * Where the pill/chip variant came from on the most recent frame: a fresh
   * Vision read, the sticky last-known cache, or none. Null until first set.
   */
  pillOcrSource: PillOcrSource | null;
  /**
   * b18-pre-ocr-gate-pip diagnostic: the backdrop average RGB the gate computed
   * for the most recent frame. This is the value the dark-frame fast path tests
   * against (< 60 on all channels -> nexus_dex_ui/drop). When a scan drops every
   * frame, this tells us WHY at a glance: dark (real dark-path trigger) vs a
   * legit dex colour (a different stall). Null until the first verdict.
   */
  lastGateAvgRgb: { r: number; g: number; b: number } | null;
  /**
   * b18-pre-ocr-gate-pip diagnostic: the PiP slab rect the gate detected for the
   * most recent frame (fractional), or null if none. Lets us confirm whether the
   * PiP was found and which zones it covered. Null until the first verdict.
   */
  lastGatePipRect: { x: number; y: number; w: number; h: number } | null;
  /** Wall-clock ms (Date.now()) of the most recent counter update. */
  updatedAt: number;
}

function emptyTelemetry(): ScanPipelineTelemetry {
  return {
    framesSkippedDuplicate: 0,
    framesPageDetectCached: 0,
    framesReceived: 0,
    framesPassedGate: 0,
    framesDroppedGate: 0,
    ocrAttempts: 0,
    ocrHits: 0,
    pillOcrSource: null,
    lastGateAvgRgb: null,
    lastGatePipRect: null,
    updatedAt: 0,
  };
}

let current: ScanPipelineTelemetry = emptyTelemetry();

/** Reset all counters. Call once at scan session start. */
export function resetScanPipelineTelemetry(): void {
  current = emptyTelemetry();
}

/** Record that a frame was received by the loop. */
export function recordScanFrameReceived(): void {
  current.framesReceived += 1;
  current.updatedAt = Date.now();
}

/**
 * Record that a frame was suppressed as a byte-identical duplicate before OCR.
 * Counted independently of framesReceived because dedup happens UPSTREAM of the
 * pre-OCR gate: a skipped duplicate never reaches the received/gate tallies, so
 * without this counter the saved OCR passes would be invisible in the trace.
 */
export function recordScanFrameSkippedDuplicate(): void {
  current.framesSkippedDuplicate += 1;
  current.updatedAt = Date.now();
}

/**
 * Vision-only trial: record where the pill/chip variant came from this frame
 * (fresh Vision read, sticky cache, or none). Replaces the deleted pre-OCR gate
 * verdict recorder.
 */
export function recordPillOcrSource(source: PillOcrSource): void {
  current.pillOcrSource = source;
  current.updatedAt = Date.now();
}

/** Record an OCR fanout invocation and whether it produced any dex numbers. */
export function recordScanOcrAttempt(hit: boolean): void {
  current.ocrAttempts += 1;
  if (hit) current.ocrHits += 1;
  current.updatedAt = Date.now();
}

/** Read a shallow copy of the current telemetry (safe to render/serialize). */
export function readScanPipelineTelemetry(): ScanPipelineTelemetry {
  return {
    ...current,
    lastGateAvgRgb: current.lastGateAvgRgb ? { ...current.lastGateAvgRgb } : null,
    lastGatePipRect: current.lastGatePipRect ? { ...current.lastGatePipRect } : null,
  };
}

/**
 * One-line human summary for the diagnostics panel. Deliberately terse so it
 * reads on a phone: "frames 120 -> gate 0 -> ocr 0 hits 0 (last: uniform_unknown)".
 * The arrow chain is the triage path: if frames > 0 but gate 0, the gate is
 * the stall; if gate > 0 but ocr hits 0, OCR is the stall.
 */
export function formatScanPipelineTelemetry(
  t: ScanPipelineTelemetry = current,
): string {
  const last = t.pillOcrSource != null ? ` (pill: ${t.pillOcrSource})` : "";
  const rgb = t.lastGateAvgRgb
    ? ` avg[${Math.round(t.lastGateAvgRgb.r)},${Math.round(t.lastGateAvgRgb.g)},${Math.round(t.lastGateAvgRgb.b)}]`
    : "";
  const pip = t.lastGatePipRect
    ? ` pip[${t.lastGatePipRect.x.toFixed(2)},${t.lastGatePipRect.y.toFixed(2)},${t.lastGatePipRect.w.toFixed(2)},${t.lastGatePipRect.h.toFixed(2)}]`
    : "";
  const dup = t.framesSkippedDuplicate > 0 ? `dup ${t.framesSkippedDuplicate} -> ` : "";
  const cached =
    t.framesPageDetectCached > 0 ? ` (page-cache ${t.framesPageDetectCached})` : "";
  return (
    `${dup}frames ${t.framesReceived} -> gate ${t.framesPassedGate}${cached}` +
    ` (dropped ${t.framesDroppedGate}) -> ocr ${t.ocrAttempts} hits ${t.ocrHits}${last}${rgb}${pip}`
  );
}
